/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { Grid, SelectChangeEvent, Typography } from "@mui/material";
import { SyntheticEvent, useState } from "react";
import ContentBox from "../ContentBox/ContentBox";
import ButtonLoading from "../Button/ButtonLoading";
import { enqueueSnackbar } from "notistack";
import ValidatorSelect from "../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import { productClassNames } from "../../types/AbstractCase";
import SearchBar from "../Algolia/SearchBar";
import { ALGOLIA_INDEX } from "../../config/_entrypoint";
import CaseHit from "../Case/CaseEntityPicker/Hit/CaseHit";
import { CaseHit as CaseHitType } from "../Case/CaseEntityPicker/Hit/hitType";
import LegalbirdIoConfirm from "../Modal/LegalbirdIoConfirm";
import { apiPost } from "../../services/Api/apiCall";
import { translate } from "../../services/Translations/translatorService";
import { EConsultDossierReimportResponse } from "../../types/EConsultDossierReimportResponse";

export default function ImportEConsultDossier() {
  const [isLoading, setIsLoading] = useState(false);
  const [productClassShortName, setProductClassShortName] = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<CaseHitType | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isPrepared, setIsPrepared] = useState(false);

  const selectCase = (caseHit: CaseHitType, event?: SyntheticEvent<HTMLElement>) => {
    if (!event) {
      return;
    }

    event.preventDefault();
    setSelectedCase(caseHit);
  };

  const handlePrepare = async () => {
    if (!selectedCase || !productClassShortName) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiPost<EConsultDossierReimportResponse>(`/lb/services/import_e_consult_dossier/prepare`, {
        caseId: selectedCase.productId,
      });

      if (!response.success) {
        throw new Error();
      }

      setIsPrepared(true);
      enqueueSnackbar("eAkte wurde für den Reimport reserviert.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } catch (error) {
      enqueueSnackbar("eAkte konnte nicht für den Reimport reserviert werden.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCase || !productClassShortName) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiPost<EConsultDossierReimportResponse>(`/lb/services/import_e_consult_dossier`, {
        caseId: selectedCase.productId,
        productClassShortName: productClassShortName,
      });

      if (response.success && response.caseLink) {
        enqueueSnackbar("eAkte wurde erfolgreich vom Fall gelöst und neu importiert.", {
          variant: "custom",
          buttonType: "link",
          buttonLink: response.caseLink,
          buttonTarget: "_self",
          buttonText: "Zum Fall",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          autoHideDuration: 10000,
        });

        setProductClassShortName(null);
        setSelectedCase(null);
        setIsPrepared(false);
      } else {
        throw new Error();
      }
    } catch (error) {
      enqueueSnackbar("eAkte konnte nicht vom Fall gelöst und neu importiert werden.", {
        variant: "error",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContentBox headline="eAkte vom Fall lösen und neu importieren">
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: "center",
        }}
      >
        <Grid size={12}>
          <Typography
            sx={{
              margin: "1rem 0",
            }}
          >
            {isPrepared
              ? "Die eAkte ist für den Reimport reserviert. Setze jetzt die ursprüngliche Nachricht in eConsult auf ungelesen und schließe den Reimport anschließend hier ab."
              : "Hier kann eine eAkte von einem falsch zugeordneten Fall gelöst und mit dem passenden Produkt neu importiert werden."}
          </Typography>
        </Grid>
        <Grid size={12}>
          <SearchBar
            searchConfig={{
              indexName: ALGOLIA_INDEX,
            }}
            resultListConfig={{
              itemSize: 330,
              width: "100%",
            }}
            HitComponent={CaseHit}
            onHitClick={selectCase}
            disabled={isLoading || isPrepared}
          />
        </Grid>
        {selectedCase && (
          <Grid size={12}>
            <Typography>
              Ausgewählter Fall: <strong>ID {selectedCase.productId}</strong>
              {selectedCase.reference ? ` – ${selectedCase.reference}` : ""}
            </Typography>
          </Grid>
        )}
        <Grid size={12}>
          <ValidatorSelect
            label="Produkt"
            value={productClassShortName}
            onChange={(event: SelectChangeEvent) => setProductClassShortName(event.target.value as string)}
            helperText={"Auswahl des Produkt als welches die eAkte importiert werden soll."}
            disabled={isPrepared}
          >
            {[...productClassNames]
              .filter((productClassName) => productClassName !== "pendingCase")
              .sort()
              .map((productClassName) => (
                <MenuItem key={productClassName} value={productClassName}>
                  {productClassName}
                </MenuItem>
              ))}
          </ValidatorSelect>
        </Grid>
        <Grid
          sx={{
            py: 3,
          }}
          size={12}
        >
          <ButtonLoading
            variant={"contained"}
            onClick={() => setIsConfirmOpen(true)}
            isLoading={isLoading}
            disabled={selectedCase === null || productClassShortName === null}
          >
            {isPrepared ? "Reimport abschließen" : "Reimport vorbereiten"}
          </ButtonLoading>
        </Grid>
      </Grid>
      <LegalbirdIoConfirm
        open={isConfirmOpen}
        handleClose={() => setIsConfirmOpen(false)}
        headline={isPrepared ? "Reimport abschließen" : "Reimport vorbereiten"}
        content={
          isPrepared ? (
            <Typography>
              Wurde die ursprüngliche Nachricht der eAkte in eConsult auf ungelesen gesetzt? Der Fall mit ID{" "}
              <strong>{selectedCase?.productId}</strong> wird anschließend als Produkt{" "}
              <strong>{productClassShortName ? translate(`${productClassShortName}.label`) : ""}</strong> neu
              importiert.
            </Typography>
          ) : (
            <Typography>
              Die eAkte von Fall mit ID <strong>{selectedCase?.productId}</strong> wird für den Reimport als Produkt{" "}
              <strong>{productClassShortName ? translate(`${productClassShortName}.label`) : ""}</strong> reserviert.
              Setze die Nachricht in eConsult erst danach auf ungelesen.
            </Typography>
          )
        }
        confirmText={isPrepared ? "Reimport abschließen" : "Reimport vorbereiten"}
        handleConfirm={isPrepared ? handleSubmit : handlePrepare}
      />
    </ContentBox>
  );
}
